import { Injectable } from '@angular/core';

/**
 * Browser-based photo capture + selection. Replaces the iOS
 * UIImagePickerController usage in SaltwaterUpAddCatchViewController.
 *
 * Strategy:
 *   - "Take Photo" → <input type="file" accept="image/*" capture="environment">
 *     On mobile this opens the camera directly. On desktop it falls back
 *     to a file picker (the `capture` attribute is a hint, not a
 *     requirement).
 *   - "Choose from Gallery" → <input type="file" accept="image/*">
 *     Opens the photo library / filesystem.
 *
 * Returns a Blob, which callers persist in Dexie's fish_stat_images
 * table and base64-encode at POST time to match the existing API.
 *
 * We resize down to a reasonable max dimension (1600px longest side) to
 * keep payload sizes manageable — a modern phone camera produces 4 MB+
 * JPEGs that would take forever to base64-encode and upload.
 */
@Injectable({ providedIn: 'root' })
export class PhotoService {
  /** Opens the device camera if available, falls back to file picker. */
  takePhoto(): Promise<Blob | null> {
    return this.pickFile(true);
  }

  /** Opens the gallery / filesystem picker. */
  choosePhoto(): Promise<Blob | null> {
    return this.pickFile(false);
  }

  /**
   * Common implementation. Creates a transient <input> element, wires
   * up a change listener, and removes the element when done. Resolving
   * to null means the user cancelled (no file chosen).
   *
   * Cancel detection in browsers is imperfect — some fire 'cancel'
   * reliably, others don't. We primarily resolve on 'change' with a
   * file, and wire 'cancel' as a best-effort fallback; if neither
   * fires, the promise simply stays unresolved until navigation away,
   * which is acceptable for a one-shot interaction.
   */
  private pickFile(camera: boolean): Promise<Blob | null> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      if (camera) {
        input.setAttribute('capture', 'environment');
      }
      input.style.display = 'none';

      const cleanup = (): void => {
        input.removeEventListener('change', onChange);
        input.removeEventListener('cancel', onCancel);
        if (input.parentNode) input.parentNode.removeChild(input);
      };

      const onChange = async (): Promise<void> => {
        const file = input.files?.[0];
        cleanup();
        if (!file) {
          resolve(null);
          return;
        }
        try {
          const resized = await this.resizeImage(file);
          resolve(resized);
        } catch {
          // If resize fails for any reason, fall back to the original.
          // Better to upload something too big than to lose the photo.
          resolve(file);
        }
      };

      const onCancel = (): void => {
        cleanup();
        resolve(null);
      };

      input.addEventListener('change', onChange);
      input.addEventListener('cancel', onCancel);

      document.body.appendChild(input);
      input.click();
    });
  }

  /**
   * Resizes the image so its longest side is at most 1600 px, keeping
   * aspect ratio. Encodes as JPEG quality 0.85. Returns the resized
   * Blob or the original if it was already small enough.
   */
  private resizeImage(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const maxDim = 1600;
      const url = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(url);

        const longest = Math.max(img.width, img.height);
        if (longest <= maxDim) {
          // Original is already small enough — use as-is.
          resolve(file);
          return;
        }

        const scale = maxDim / longest;
        const width = Math.round(img.width * scale);
        const height = Math.round(img.height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas 2D context unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas toBlob returned null'));
            }
          },
          'image/jpeg',
          0.85,
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image for resizing'));
      };

      img.src = url;
    });
  }

  /**
   * Converts a Blob to base64-encoded string (no data: prefix), matching
   * the `image_data` field the iOS app sends in the fishStats.json POST.
   * Used at save time; we don't store images in base64 form in IndexedDB.
   */
  blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // result is "data:image/jpeg;base64,XXXX" — strip the prefix.
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
      reader.readAsDataURL(blob);
    });
  }
}
